<template>

    <div ref="gridContainer">
        <!-- DRAG AND DROP OPTIONS -->
        <div class="grid">

            <div v-for="(item, index) in tableOptions"
                 class="item"
                 ref="option"
                 :data-id="item.uuid"
                 :key="item.uuid">

                <v-sheet rounded color="grey darken-3" class="item-content">
                    <v-scale-transition>
                        <div class="choice-box d-flex">

                            <v-icon class="drag-handle align-self-center">mdi-drag</v-icon>

                            <v-chip label class="ml-2 mr-0 align-self-center">{{ item.order }}</v-chip>

                            <v-text-field
                                dense
                                color="primary"
                                rounded
                                background-color="transparent"
                                hide-details
                                autocomplete="false"
                                placeholder="Please enter option name"
                                class="elevation-0 flex-grow-1 mr-2 align-self-center mb-1"
                                v-model="item.name"
                                ref="textField"
                                @mousedown="optionNameClicked(item)"
                                @blur="optionNameChanged(item)"
                            />

                            <v-btn v-if="enableDelete"
                                   color="transparent"
                                   fab
                                   x-small
                                   tabindex="-1"
                                   class="elevation-0 align-self-center mr-1 ml-1"
                                   @click="deleteOptionClicked(item, index)">
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </div>
                    </v-scale-transition>
                </v-sheet>
            </div>
        </div>

        <div class="text-right mt-4">

            <!-- PASTE OPTIONS BUTTON -->
            <v-tooltip top max-width="300" open-delay="200" color="primary" v-if="enableAdd && hasOptions">
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" color="primary" small class="mr-1" @click="pasteOptionsClicked">
                        <v-icon left>mdi-content-copy</v-icon>
                        Paste options
                    </v-btn>
                </template>
                Click here to paste in option texts i.e. from Word document, Excel etc..
            </v-tooltip>

            <!-- ADD NEW OPTION BUTTON -->
            <v-btn v-if="enableAdd"
                   color="primary"
                   small
                   ref="addButton"
                   class="mr-2"
                   @click="addOption()">
                <v-icon left>mdi-plus</v-icon>
                Add option
            </v-btn>
        </div>

        <!-- CONFIRMATION DIALOG -->
        <confirmation-dialog v-model="deleteConfirmationDialogModel"
                             headline="DELETE CONFIRMATION"
                             delete
                             confirm-button-text="Delete"
                             abort-button-text="Cancel"
                             @selected="deleteConfirmationClicked">

            Deleting data option can potentially be very dangerous and cause permanent data loss!

            <stat-box
                class="mt-3"
                :value="deleteOptionDataCount"
                hint="Total number of records to be deleted"
                color="error"
                :loading="deleteOptionDataCountLoading"
            ></stat-box>

        </confirmation-dialog>

        <paste-options-dialog v-if="hasOptions"
                              v-model="pasteOptionDialogModel"
                              :min-options="optionCount"
                              @change="optionsPasted"
                              :name="name"
        ></paste-options-dialog>

        <!-- DEBUG -->
        <json v-show="false" :data="dataVariable"></json>

    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapActions} from 'vuex';
import StatBox from '../../../components/UI/StatBoxNumerical';
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
import Muuri from 'muuri';
import PasteOptionsDialog from "./PasteOptionsDialog";
import {v4 as uuid} from 'uuid';

export default {

    components: {
        PasteOptionsDialog,
        Json,
        StatBox,
        ConfirmationDialog
    },

    props: {
        dataVariable: {
            type: Object | null,
            required: true
        },
        availableLocales: {
            type: Array | null,
            required: true
        },
        errorMessages: {
            type: Object,
            required: false,
            default: () => {
            }
        },
        enableAdd: {
            type: Boolean,
            required: false,
            default: true,
        },
        enableDelete: {
            type: Boolean,
            required: false,
            default: true
        },
        name: {
            required: true,
            type: String,
            validator: (value) => ['data variable', 'question'].includes(value)
        }
    },

    watch: {
        dataVariable(variable) {
            this.buildTableOptions();
        }
    },

    mounted() {
        if (this.dataVariable.options) {
            this.buildTableOptions();

            this.$nextTick(() => {
                this.createMuuriInstance();
            });
        }
    },

    computed: {

        hasOptions() {
            return this.dataVariable && (this.dataVariable.type === 'radio' || this.dataVariable.type === 'checkbox');
        },

        optionCount() {
            return this.tableOptions.length;
        },

        // Find data variable locales based on locales question_text options
        options() {
            let locales = [];
            if (this.dataVariable && this.availableLocales) {
                this.dataVariable.locales.forEach(locale => {
                    const found = this.availableLocales.find(l => locale.locale_id === l.id);
                    if (found === undefined) {
                        throw new Error('Question option locale could not be found!');
                    }
                    locales.push(found);
                });
            }
            return locales;
        },
    },

    data() {
        return {
            pasteOptionDialogModel: false,
            optionUnderEdit: null,

            deleteConfirmationDialogModel: false,
            deleteOption: null,
            deleteOptionIndex: null,
            deleteOptionDataCount: 0,
            deleteOptionDataCountLoading: false,

            tableOptions: [],
            maxchars: v => v.length <= 100 || 'Input name is too long!',
            headers: [
                {
                    text: 'Option name',
                    align: 'left',
                    sortable: false,
                    value: 'name'
                },
                {
                    text: 'Actions',
                    align: 'right',
                    sortable: false,
                    value: null,
                }
            ]
        }
    },

    methods: {

        // Root actions
        ...mapActions([
            'getDataVariableOptionDataCount',
        ]),

        buildTableOptions() {
            this.tableOptions = JSON.parse(JSON.stringify(this.dataVariable.options))
                .sort((a, b) => {
                    return a.order - b.order;
                })
                .map(option => {

                    // Add UUID if it doesn't exit yet
                    if (!option.uuid) option.uuid = uuid();

                    return option;
                });
        },

        optionsPasted(optionNames) {

            //if (this.grid) this.grid.destroy();
            //this.createMuuriInstance();

            optionNames.forEach((name, index) => {
                // Option exists, update name
                if (this.tableOptions[index]) {
                    this.optionNameClicked(this.tableOptions[index]);
                    this.tableOptions[index].name = name;
                    this.optionNameChanged(this.tableOptions[index]);
                } else {
                    console.log("ADDING OPTION "+name);
                    this.addOption(name);
                }
            });
        },

        pasteOptionsClicked() {
            this.pasteOptionDialogModel = true;
        },

        optionNameClicked(option) {
            this.optionUnderEdit = JSON.parse(JSON.stringify(option));
        },

        optionNameChanged(option) {

            this.updateLocalesToMatchName(option);

            const data = {
                updatedOption: Object.assign({}, option),
                oldOption: Object.assign({}, this.optionUnderEdit)
            };

            this.$emit('option:updated', data);
            this.optionUnderEdit = null;
        },

        updateLocalesToMatchName(option) {
            // Skip for newly added options
            if (this.optionUnderEdit) {
                option.locales.forEach(locale => {
                    if (locale.option_text === this.optionUnderEdit.name) {
                        locale.option_text = option.name;
                    }
                })
            }
        },

        addOption(name = null) {

            if (!this.availableLocales) {
                Bus.$emit('flash-message', {type: 'error', message: 'Cannot add new options at this time'});
                return;
            }

            const optionName = !name ? "" : name;

            const optionUuid = uuid();

            let newOption = {
                id: null,
                order: this.tableOptions.length + 1,
                name: optionName,
                locales: [],
                uuid: optionUuid
            };

            this.options.forEach(locale => {

                const optionText = !name ? '' : name;

                newOption.locales.push({
                    id: newOption.locales.length + 1,
                    option_text: optionText,
                    locale_id: locale.id,
                    uuid: uuid()
                });
            });

            this.tableOptions.push(newOption);

            this.lockContainerHeight();

            this.optionUnderEdit = newOption;

            const elementIndex = this.tableOptions.length - 1;

            this.$nextTick(() => {

                this.grid.add(this.$refs.option[elementIndex])

                this.unlockContainerHeight();

                this.$nextTick(() => {
                    this.$refs.textField[elementIndex].focus();
                    this.$nextTick(() => {
                        this.$refs.addButton.$el.scrollIntoView();
                    })
                })
            });

            this.emit();
        },

        lockContainerHeight() {
            const height = this.$refs.gridContainer.offsetHeight + 50;
            this.$refs.gridContainer.style.height = height + "px";
        },

        unlockContainerHeight() {
            this.$refs.gridContainer.style.height = null;
        },

        deleteOptionClicked(option, index) {

            if (this.tableOptions.length === 1) {
                Bus.$emit('flash-message', {type: 'error', message: 'Sorry, you cannot remove the last option!'});
                return;
            }

            this.deleteOption = option;
            this.deleteOptionIndex = index;

            if (this.deleteOption.id !== null) {

                this.deleteOptionDataCountLoading = true;

                this.getDataVariableOptionDataCount({
                    dataVariableId: this.dataVariable.id,
                    optionId: this.tableOptions[index].id
                })
                    .then(data => {
                        this.deleteOptionDataCount = data.count;
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {
                            type: 'error',
                            message: 'Failed loading stored data count for the option!'
                        });
                    })
                    .finally(() => {
                        this.deleteOptionDataCountLoading = false;
                    });

                this.deleteConfirmationDialogModel = true;
            } else {
                this.deleteOptionDo();
            }
        },

        deleteConfirmationClicked(confirmed) {
            this.deleteConfirmationDialogModel = false;

            if (confirmed) {
                this.deleteOptionDo();
            }
        },

        deleteOptionDo() {

            let found = this.grid.getItems().find(item => this.deleteOption.uuid === item.getElement().getAttribute('data-id'));

            this.grid.remove([found]);

            this.tableOptions.splice(this.deleteOptionIndex, 1);

            this.emit();
        },

        createMuuriInstance() {

            this.grid = new Muuri('.grid', {
                dragAxis: 'y',
                dragEnabled: true,
                layout: {
                    fillGaps: true
                },
                dragHandle: '.drag-handle'
            });

            this.grid.on('dragReleaseEnd', items => {
                this.updateOrder(items)
            });
        },

        updateOrder() {

            let uuids = this.grid.getItems().map(item => item.getElement().getAttribute('data-id'));

            let order = 1;

            uuids.forEach(uuid => {

                let option = this.tableOptions.find(option => option.uuid === uuid);

                option.order = order;
                order++;
            })

            this.emit();
        },

        emit() {
            this.$emit('change', [...this.tableOptions]);
        }
    },
}
</script>

<style scoped>

tr:hover {
    background-color: transparent !important;
}

.grid {
    position: relative;
    transition: opacity 0.8s linear 1s;
    height: 100%;
}

.item {
    display: block;
    position: absolute;
    z-index: 1;
    margin: 1px;
    height: 50px;
    width: calc(100% - 10px);
}

.item.muuri-item-hidden {
    z-index: 0;
}

.item.muuri-item-positioning {
    z-index: 2;
}

.item.muuri-item-releasing {
    z-index: 3;
}

.item.muuri-item-dragging {
    z-index: 4;
}

.item-content {
    position: relative;
    width: 100%;
    height: 50px;
    text-align: center;
}

.item.muuri-item-dragging .item-content {

}

.item.muuri-item-releasing .item-content {

}

.choice-box {
    height: 50px;
}

.drag-handle {
    width: 30px;
    cursor: pointer;
}

</style>